import { z } from 'zod';
import { ModalGoalValidationSchema } from './GoalAndOperationalPlansForm/Goals/validators';
import {
  EMPTY_FIELD_MESSAGE,
  validateEditorEn,
  validateSpace,
  validateSpaceTextInput,
  validateTextEn,
} from '@Utils/validation';
import { uploadImageSchema } from '@Containers/UploadImageFormFields/validators';

enum COMPANY_VALUE_INCREASE_PLANS_MESSAGE {
  INTRODUCTION_EN = 'กรุณาระบุคำบรรยายแผนการเพิ่มมูลค่าองค์กร และเป้าหมายเป็นภาษาอังกฤษ',
  REMARK_EN = 'กรุณาระบุหมายเหตุแผนการเพิ่มมูลค่าองค์กร และเป้าหมายเป็นภาษาอังกฤษ',
  ASSETS = 'กรุณาเลือกไฟล์ภาพเป้าหมาย',
  DESCRIPTION_ASSETS_EN = 'กรุณาระบุชื่อรูปภาพ/หัวข้อ/คำอธิบายเพิ่มเติมเป็นภาษาอังกฤษ',
}

enum VALUE_UP_PLAN_MESSAGE {
  NAME = 'กรุณาระบุชื่อแผน',
  NAME_EN = 'กรุณาระบุแผนเป็นภาษาอังกฤษ',
  SIGNIFICANCE = 'กรุณาอธิบายที่มา และความสำคัญ',
  SIGNIFICANCE_EN = 'กรุณาอธิบายที่มา และความสำคัญเป็นภาษาอังกฤษ',
  RELATE_GOAL = 'กรุณาเลือกหัวข้อที่เกี่ยวข้องอย่างน้อย 1 ตัวเลือก',
}

enum RISK_MESSAGE {
  INTRODUCTION_EN = 'กรุณาระบุคำบรรยายการบริหารความเสี่ยงเป็นภาษาอังกฤษ',
  REMARK_EN = 'กรุณาระบุหมายเหตุการบริหารความเสี่ยงเป็นภาษาอังกฤษ',
  NAME = 'กรุณาระบุชื่อความเสี่ยง',
  NAME_EN = 'กรุณาระบุชื่อความเสี่ยงเป็นภาษาอังกฤษ',
  CHANCE = 'กรุณาเลือกโอกาสเกิดความเสี่ยง',
  RELATE_RISK = 'กรุณาเลือกหัวข้อความเสี่ยงที่เกี่ยวข้องอย่างน้อย 1 ตัวเลือก',
  CHARACTERISTIC = 'กรุณาอธิบายลักษณะความเสี่ยง และ ผลกระทบ',
  CHARACTERISTIC_EN = 'กรุณาอธิบายลักษณะความเสี่ยง และ ผลกระทบ เป็นภาษาอังกฤษ',
  MEASUREMENT_EN = 'กรุณาอธิบายมาตรการจัดการความเสี่ยงเป็นภาษาอังกฤษ',
}

enum GOAL_MESSAGE {
  GOAL = 'กรุณาเพิ่มข้อมูลเป้าหมาย',
}

const IUploadImageSchema = uploadImageSchema({
  assets: COMPANY_VALUE_INCREASE_PLANS_MESSAGE.ASSETS,
  descriptionAssetsEn:
    COMPANY_VALUE_INCREASE_PLANS_MESSAGE.DESCRIPTION_ASSETS_EN,
});

const IntroductionSchema = z.object({
  descriptionTh: z
    .string()
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR)
    .nullable()
    .optional(),
  descriptionEn: z
    .string()
    .regex(
      validateEditorEn,
      COMPANY_VALUE_INCREASE_PLANS_MESSAGE.INTRODUCTION_EN
    )
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR)
    .nullable()
    .optional(),
});

const RemarkSchema = z.object({
  descriptionTh: z
    .string()
    .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
    .nullable()
    .optional(),
  descriptionEn: z
    .string()
    .regex(validateTextEn, COMPANY_VALUE_INCREASE_PLANS_MESSAGE.REMARK_EN)
    .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
    .nullable()
    .optional(),
});

const RelateMasterDataSchema = z.object({
  infoCategoryMasterDataId: z.number(),
});

const RiskSchema = z.object({
  nameTh: z
    .string({ message: RISK_MESSAGE.NAME })
    .min(1, { message: RISK_MESSAGE.NAME })
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR),
  nameEn: z
    .string({ message: RISK_MESSAGE.NAME })
    .min(1, { message: RISK_MESSAGE.NAME })
    .regex(validateTextEn, RISK_MESSAGE.NAME_EN)
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR),
  chance: z.enum(['HIGH', 'MEDIUM', 'LOW'], { message: RISK_MESSAGE.CHANCE }),
  relateRisks: z
    .array(RelateMasterDataSchema, { message: RISK_MESSAGE.RELATE_RISK })
    .min(1, { message: RISK_MESSAGE.RELATE_RISK }),
  characteristicTh: z
    .string({ message: RISK_MESSAGE.CHARACTERISTIC })
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR),
  characteristicEn: z
    .string({ message: RISK_MESSAGE.CHARACTERISTIC })
    .regex(validateEditorEn, RISK_MESSAGE.CHARACTERISTIC_EN)
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR),
  measurementTh: z.string().nullable().optional(),
  measurementEn: z
    .string()
    .regex(validateEditorEn, RISK_MESSAGE.MEASUREMENT_EN)
    .nullable()
    .optional(),
});

const RiskManagementSchema = z.object({
  introduction: z.object({
    descriptionTh: z
      .string()
      .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR)
      .nullable()
      .optional(),
    descriptionEn: z
      .string()
      .regex(validateEditorEn, RISK_MESSAGE.INTRODUCTION_EN)
      .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR)
      .nullable()
      .optional(),
  }),
  remark: z.object({
    descriptionTh: z
      .string()
      .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
      .nullable()
      .optional(),
    descriptionEn: z
      .string()
      .regex(validateTextEn, RISK_MESSAGE.REMARK_EN)
      .refine(validateSpaceTextInput, EMPTY_FIELD_MESSAGE.SPACE_BAR)
      .nullable()
      .optional(),
  }),
  risks: z.array(RiskSchema),
});

const ValueUpPlanSchema = z.object({
  nameTh: z
    .string({ message: VALUE_UP_PLAN_MESSAGE.NAME })
    .min(1, { message: VALUE_UP_PLAN_MESSAGE.NAME })
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR),
  nameEn: z
    .string({ message: VALUE_UP_PLAN_MESSAGE.NAME })
    .min(1, { message: VALUE_UP_PLAN_MESSAGE.NAME })
    .regex(validateTextEn, VALUE_UP_PLAN_MESSAGE.NAME_EN)
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR),
  significanceTh: z
    .string({ message: VALUE_UP_PLAN_MESSAGE.SIGNIFICANCE })
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR),
  significanceEn: z
    .string({ message: VALUE_UP_PLAN_MESSAGE.SIGNIFICANCE })
    .regex(validateEditorEn, VALUE_UP_PLAN_MESSAGE.SIGNIFICANCE_EN)
    .refine(validateSpace, EMPTY_FIELD_MESSAGE.SPACE_BAR),
  relateGoals: z
    .array(RelateMasterDataSchema, {
      message: VALUE_UP_PLAN_MESSAGE.RELATE_GOAL,
    })
    .min(1, { message: VALUE_UP_PLAN_MESSAGE.RELATE_GOAL }),
  riskManagement: RiskManagementSchema,
  goals: z
    .array(ModalGoalValidationSchema, {
      message: GOAL_MESSAGE.GOAL,
    })
    .nonempty({ message: GOAL_MESSAGE.GOAL }),
  assets: z.array(IUploadImageSchema),
});

export const CompanyValueIncreasePlansSchema = z.object({
  introduction: IntroductionSchema.optional(),
  remark: RemarkSchema.optional(),
  valueUpPlans: z.array(ValueUpPlanSchema),
});
